<template>
  <div class="file-drop-container">
    <label v-if="label" class="file-drop-label">{{ label }}<span v-if="required" class="required-indicator">*</span></label>
    <div
      class="file-drop-zone"
      :class="zoneClass"
      @click="triggerFileInput"
      @dragover.prevent="handleDragOver"
      @dragleave.prevent="handleDragLeave"
      @drop.prevent="handleDrop"
    >
      <input
        ref="fileInput"
        type="file"
        :accept="accept"
        :multiple="multiple"
        @change="handleFileSelect"
        class="file-input-hidden"
      />
      <template v-if="!hasFiles">
        <div class="drop-icon-wrap">
          <vue-feather type="upload" size="20" class="drop-icon" />
        </div>
        <span class="drop-text">{{ isDragOver ? 'Drop to upload' : placeholder }}</span>
        <span v-if="hint" class="drop-hint">{{ hint }}</span>
      </template>
      <template v-else>
        <div class="file-list">
          <div v-for="(file, index) in fileList" :key="index" class="file-item">
            <vue-feather type="file-text" size="14" class="file-item-icon" />
            <span class="file-item-name">{{ file.name }}</span>
            <span class="file-item-size">{{ formatSize(file.size) }}</span>
            <button class="file-item-remove" @click.stop="removeFile(index)" type="button">
              <vue-feather type="x" size="12" />
            </button>
          </div>
        </div>
      </template>
    </div>
    <span v-if="error" class="file-drop-error">{{ error }}</span>
  </div>
</template>

<script>
import VueFeather from 'vue-feather'

export default {
  name: 'FileDropZone',
  components: {
    'vue-feather': VueFeather
  },
  props: {
    modelValue: {
      type: [File, Array, null],
      default: null
    },
    label: {
      type: String,
      default: ''
    },
    placeholder: {
      type: String,
      default: 'Drag & drop files here, or click to browse'
    },
    hint: {
      type: String,
      default: ''
    },
    accept: {
      type: String,
      default: '*/*'
    },
    multiple: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    error: {
      type: String,
      default: ''
    },
    required: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isDragOver: false
    }
  },
  computed: {
    zoneClass() {
      return {
        'file-drop-zone--has-files': this.hasFiles,
        'file-drop-zone--drag-over': this.isDragOver,
        'file-drop-zone--disabled': this.disabled,
        'file-drop-zone--error': this.error
      }
    },
    hasFiles() {
      if (this.multiple) {
        return Array.isArray(this.modelValue) && this.modelValue.length > 0
      }
      return !!this.modelValue
    },
    fileList() {
      if (this.multiple) {
        return Array.isArray(this.modelValue) ? this.modelValue : []
      }
      return this.modelValue ? [this.modelValue] : []
    }
  },
  methods: {
    triggerFileInput() {
      if (this.disabled) return
      this.$refs.fileInput.click()
    },
    handleFileSelect(event) {
      const files = Array.from(event.target.files)
      if (files.length > 0) {
        this.validateAndEmit(files)
      }
    },
    handleDragOver() {
      if (this.disabled) return
      this.isDragOver = true
    },
    handleDragLeave() {
      this.isDragOver = false
    },
    handleDrop(event) {
      this.isDragOver = false
      if (this.disabled) return
      const files = Array.from(event.dataTransfer.files)
      if (files.length > 0) {
        this.validateAndEmit(files)
      }
    },
    validateAndEmit(files) {
      if (this.accept && this.accept !== '*/*') {
        const acceptTypes = this.accept.split(',').map(t => t.trim())
        const validFiles = files.filter(file => {
          const fileType = file.type
          const fileExt = '.' + file.name.split('.').pop().toLowerCase()
          return acceptTypes.some(type => {
            if (type.startsWith('.')) return fileExt === type.toLowerCase()
            if (type.endsWith('/*')) return fileType.startsWith(type.replace('/*', '/'))
            return fileType === type
          })
        })

        const invalidCount = files.length - validFiles.length
        if (invalidCount > 0) {
          this.$emit('invalid-file', files.filter(f => !validFiles.includes(f)))
        }

        if (validFiles.length === 0) return
        files = validFiles
      }

      if (this.multiple) {
        const existing = Array.isArray(this.modelValue) ? this.modelValue : []
        this.$emit('update:modelValue', [...existing, ...files])
      } else {
        this.$emit('update:modelValue', files[0])
      }
    },
    removeFile(index) {
      if (this.multiple) {
        const updated = [...this.modelValue]
        updated.splice(index, 1)
        this.$emit('update:modelValue', updated.length > 0 ? updated : [])
      } else {
        this.$refs.fileInput.value = ''
        this.$emit('update:modelValue', null)
      }
    },
    formatSize(bytes) {
      if (bytes < 1024) return bytes + ' B'
      if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
      return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
    }
  },
  emits: ['update:modelValue', 'invalid-file']
}
</script>

<style scoped>
.file-drop-container {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.file-drop-label {
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
}

.required-indicator {
  color: #dc2626;
  margin-left: 2px;
}

.file-input-hidden {
  display: none;
}

.file-drop-zone {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 160px;
  padding: 32px;
  background: #f9fafb;
  border: 1.5px dashed #d1d5db;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s ease;
  box-sizing: border-box;
}

.file-drop-zone:hover:not(.file-drop-zone--disabled) {
  border-color: #9ca3af;
  background: #f3f4f6;
}

.file-drop-zone--drag-over:not(.file-drop-zone--disabled) {
  border-color: var(--color-primary, #6a16f0);
  background: #f5f3ff;
  box-shadow: 0 0 0 3px rgba(106, 22, 240, 0.1);
}

.file-drop-zone--drag-over .drop-icon-wrap {
  background: rgba(106, 22, 240, 0.1);
}

.file-drop-zone--drag-over .drop-icon {
  color: var(--color-primary, #6a16f0);
}

.file-drop-zone--drag-over .drop-text {
  color: var(--color-primary, #6a16f0);
}

.file-drop-zone--has-files {
  align-items: stretch;
  padding: 10px;
  min-height: auto;
}

.file-drop-zone--disabled {
  background: #fafafa;
  cursor: not-allowed;
}

.file-drop-zone--error {
  border-color: #dc2626;
}

/* Empty state */
.drop-icon-wrap {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: #e9ebee;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 4px;
  transition: all 0.15s ease;
}

.file-drop-zone:hover:not(.file-drop-zone--disabled) .drop-icon-wrap {
  background: #dddfe3;
}

.drop-icon {
  color: #6b7280;
  transition: color 0.15s ease;
}

.drop-text {
  font-size: 0.9375rem;
  font-weight: 500;
  color: #4b5563;
  transition: color 0.15s ease;
}

.drop-hint {
  font-size: 0.75rem;
  color: #9ca3af;
}

/* File list */
.file-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
}

.file-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  background: #fafafa;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
}

.file-item-icon {
  color: #374151;
  flex-shrink: 0;
}

.file-item-name {
  flex: 1;
  font-size: 1rem;
  color: #374151;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.file-item-size {
  font-size: 0.8125rem;
  color: #9ca3af;
  flex-shrink: 0;
}

.file-item-remove {
  width: 24px;
  height: 24px;
  border: none;
  background: transparent;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  transition: all 0.15s ease;
  flex-shrink: 0;
}

.file-item-remove:hover {
  background: #fee2e2;
  color: #dc2626;
}

.file-drop-error {
  font-size: 0.8125rem;
  color: #dc2626;
}
</style>
